import { CertipersUserStatus } from "@/const/user";
import { UserSession, Role } from "@/models";
import { ROLE_TYPE } from "@/models/enums/roleType";
import { USER_GROUPS } from "@/models/enums/userGroups";
import { ROLE_STATUS } from "@/models/enums/roleStatuses";

import { Model } from "pinia-orm";
import { Agency } from "@/models/socio-model";

import { SOCOTEC_GROUPS, DEVELOPERS_GROUPS, SUPPORT_GROUPS } from "./socio-model/utils/usersRoles";

export default class CertipersUser extends Model {
  static entity = "certipersUsers";
  static primaryKey = "uuid";

  static fields() {
    return {
      uuid: this.uid(""),
      agency: this.attr(null), // Foreign key
      cloudId: this.attr(null),
      email: this.string(""),
      firstName: this.string(""),
      groups: this.attr([]),
      isSuperUser: this.boolean(false),
      isCurrentUser: this.boolean(false),
      jobTitle: this.string(""),
      lastName: this.string(""),
      signature: this.attr(null),
      telephoneNumber: this.string(""),
      lastLogin: this.string(""),
      agencyDatas: this.belongsTo(Agency, "agency"),
      socotecCode: this.string(""),
      usermanagementUuid: this.uid(""),
      birthDate: this.string(""),

      status: this.string(CertipersUserStatus.PENDING),
      isDisplayed: this.boolean(true),

      mobileTelephoneNumber: this.attr(""),
      mobileTelephoneNumberPro: this.attr(""),
      adress: this.string(""),
      postalCode: this.string(""),
      city: this.string(""),
      emailPro: this.string(""),
      password: this.string(""),
      passwordConfirm: this.string(""),
      agencyCode: this.string(""),

      companyName: this.string(""),
      companySiret: this.string(""),
      companyAdress: this.string(""),
      companyPostalCode: this.string(""),
      companyCity: this.string(""),
      companyPhone: this.string(""),
      companyEmailPro: this.string(""),

      billingUseCompanyInformation: this.boolean(false),
      billingName: this.string(""),
      billingSiret: this.string(""),
      billingAdress: this.string(""),
      billingPostalCode: this.string(""),
      billingCity: this.string(""),
      billingPhone: this.string(""),
      billingEmailPro: this.string(""),
      billingTvaNumber: this.string(""),

      bookCompanyUseCompanyInformation: this.boolean(false),
      bookAdress: this.string(""),
      bookPostalCode: this.string(""),
      bookCity: this.string(""),
      bookPhone: this.string(""),
      bookEmailPro: this.string(""),
      isDuplicatedUser: this.boolean(false),
      qrCodeUuid: this.attr(null),

      userSessionsList: this.hasMany(UserSession, "userUuid"),

      roles: this.hasMany(Role, "userUuid"),
    };
  }

  useCompanyForBook() {
    this.copyCompany("book");
  }

  useCompanyForBilling() {
    this.copyCompany("billing");
  }

  copyCompany(type) {
    let typeKeys = Object.keys(this).filter(
      (key) => key.startsWith(type) && !key.startsWith(`${type}CompanyUse`),
    );
    for (const key of typeKeys) {
      const companyKeyMatch = key.replace(type, "company");
      if (this[companyKeyMatch]) {
        this[key] = this[companyKeyMatch];
      }
    }
  }
  isRoleReviewer() {
    // NL - 2023-04-18 - Check if the condition on validated status should be kept
    return this.roles.some((element) => {
      if (element.roleType === ROLE_TYPE.REVIEWER && element.status === ROLE_STATUS.ACTIVE) {
        return true;
      }
    });
  }
  isRoleAdmin() {
    return this.groups.includes(USER_GROUPS.APPMANAGER);
  }
  isRoleSuperAdmin() {
    return this.groups.includes(USER_GROUPS.SUPERAPPMANAGER);
  }

  isfromSOCOTEC() {
    return this.groups.includes(USER_GROUPS.SOCOTECUSER);
  }

  get isDev() {
    return this.isInGroups(DEVELOPERS_GROUPS);
  }

  get isSupport() {
    return this.isSuperUser || this.isDev() || this.isInGroups(SUPPORT_GROUPS);
  }

  get isRapsotecSupport() {
    return this.isInGroup(SOCOTEC_GROUPS.SUPPORT_RAPSOTEC);
  }

  get isRapsospsSupport() {
    return this.isInGroup(SOCOTEC_GROUPS.SUPPORT_RAPSOSPS);
  }

  get isTechnicalDirection() {
    return this.isInGroup(SOCOTEC_GROUPS.TECHNICAL_DIRECTION);
  }

  get isNationalManager() {
    return this.isInGroup(SOCOTEC_GROUPS.NATIONAL_MANAGER);
  }

  get isActivityDirector() {
    return this.isInGroup(SOCOTEC_GROUPS.ACTIVITY_DIRECTOR);
  }

  isInGroup(groupToCheck) {
    return this.groups.includes(groupToCheck);
  }

  isInGroups(groupsToCheck) {
    return this.groups.some((group) => groupsToCheck.includes(group));
  }

  get isAgencyManager() {
    return this.isInGroup(SOCOTEC_GROUPS.AGENCY_MANAGER);
  }
}
